import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import contentSeriesService from '../../../services/contentSeriesService';
import DashboardLayout from '../../../components/layout/DashboardLayout';

interface Post {
  id: string;
  title: string;
  content: string;
  publishedAt?: string;
  tags?: string[];
}

interface SeriesWithDetails {
  id: string;
  title: string;
  description?: string;
  isPublic: boolean;
  coverImage?: string;
  tags: string[];
  posts: Post[];
  stats: {
    totalPosts: number;
    totalLikes: number;
    totalComments: number;
    averageEngagement: number;
  };
}

const SeriesEditorPage: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const [series, setSeries] = useState<SeriesWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [availablePosts, setAvailablePosts] = useState<Post[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<string>('');

  useEffect(() => {
    loadSeries();
    loadAvailablePosts();
  }, [seriesId]);

  const loadSeries = async () => {
    if (!seriesId) return;
    try {
      setLoading(true);
      const data = await contentSeriesService.getSeries(seriesId);
      setSeries(data);
    } catch (error) {
      console.error('Error loading series:', error);
      alert('Failed to load series');
      navigate('/creator/series');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePosts = async () => {
    // In a real app, fetch user's posts from API
    // For now, just using empty array
    setAvailablePosts([]);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (dropIndex: number) => {
    if (draggedIndex === null || !series || draggedIndex === dropIndex) return;

    const newPosts = [...series.posts];
    const [draggedPost] = newPosts.splice(draggedIndex, 1);
    newPosts.splice(dropIndex, 0, draggedPost);

    // Update order in database
    const postOrders = newPosts.map((post, index) => ({
      postId: post.id,
      order: index,
    }));

    try {
      await contentSeriesService.reorderSeries(series.id, postOrders);
      setSeries({ ...series, posts: newPosts });
    } catch (error) {
      console.error('Error reordering posts:', error);
      alert('Failed to reorder posts');
    }

    setDraggedIndex(null);
  };

  const handleRemovePost = async (postId: string) => {
    if (!series) return;
    if (!confirm('Remove this post from the series?')) return;

    try {
      const updated = await contentSeriesService.removePostFromSeries(series.id, postId);
      setSeries(updated);
    } catch (error) {
      console.error('Error removing post:', error);
      alert('Failed to remove post');
    }
  };

  const handleAddPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!series || !selectedPost) return;

    try {
      const updated = await contentSeriesService.addPostToSeries(series.id, selectedPost);
      setSeries(updated);
      setShowAddModal(false);
      setSelectedPost('');
    } catch (error) {
      console.error('Error adding post:', error);
      alert('Failed to add post');
    }
  };

  const handleUpdateMetadata = () => {
    navigate(`/creator/series/${seriesId}/settings`);
  };

  if (loading) {
    return (
      <DashboardLayout userType="creator">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!series) {
    return (
      <DashboardLayout userType="creator">
        <div className="text-center py-12">
          <p className="text-gray-600">Series not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="creator">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <button
                onClick={() => navigate('/creator/series')}
                className="text-blue-600 hover:text-blue-700 mb-2 inline-flex items-center text-sm"
              >
                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Series
              </button>
              <h1 className="text-3xl font-bold text-gray-900">{series.title}</h1>
              <p className="mt-2 text-gray-600">Manage posts in this series</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleUpdateMetadata}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Settings
              </button>
              <button
                onClick={() => navigate(`/series/${series.id}`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Preview
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Posts</p>
            <p className="text-2xl font-bold text-gray-900">{series.stats.totalPosts}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Total Likes</p>
            <p className="text-2xl font-bold text-gray-900">{series.stats.totalLikes}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Total Comments</p>
            <p className="text-2xl font-bold text-gray-900">{series.stats.totalComments}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-600">Avg Engagement</p>
            <p className="text-2xl font-bold text-gray-900">{series.stats.averageEngagement}</p>
          </div>
        </div>

        {/* Posts List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Posts in Series</h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Post
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1">Drag and drop to reorder</p>
          </div>

          {series.posts.length === 0 ? (
            <div className="p-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No posts yet</h3>
              <p className="mt-1 text-sm text-gray-500">Add posts to this series to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {series.posts.map((post, index) => (
                <div
                  key={post.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                  className={`p-4 hover:bg-gray-50 cursor-move transition-colors ${
                    draggedIndex === index ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                      </svg>
                    </div>
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-medium text-gray-900 truncate">
                        {post.title || 'Untitled'}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-1">{post.content}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {post.publishedAt && (
                          <span>
                            📅 {new Date(post.publishedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => window.open(`/post/${post.id}`, '_blank')}
                        className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                        title="View"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemovePost(post.id)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        title="Remove"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Post Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Add Post to Series</h2>
              <form onSubmit={handleAddPost}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Post
                  </label>
                  <select
                    value={selectedPost}
                    onChange={(e) => setSelectedPost(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Choose a post...</option>
                    {availablePosts.map(post => (
                      <option key={post.id} value={post.id}>
                        {post.title || 'Untitled'}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    The post will be added to the end of the series. You can reorder it by dragging.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setSelectedPost('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SeriesEditorPage;
